import { addDays, diffDays, isWeekend, toISODate } from "../../../utils/weekDates";
import { countLeaveDays } from "../../../utils/leaveDays";
import { sortByAccessor } from "../../../utils/sortRows";
import { dateOf, normalizeLeave } from "../schedule/scheduleConfig";
import { columnKeyFor, getFieldValue, plainCustomValue } from "../../../utils/customFieldUtils";

// The People off row turns red on days this many people (or more) are away.
export const CLASH_THRESHOLD = 3;

export const STATUS_LABEL = { pending: "Pending", approved: "Approved", rejected: "Rejected", cancelled: "Cancelled" };
export const STATUS_ORDER = Object.values(STATUS_LABEL);
// dot colours the group heading, bg/fg the status pill.
export const STATUS_STYLE = {
  Pending: { dot: "#f59e0b", bg: "#fef3c7", fg: "#92400e" },
  Approved: { dot: "#16a34a", bg: "#dcfce7", fg: "#166534" },
  Rejected: { dot: "#dc2626", bg: "#fee2e2", fg: "#991b1b" },
  Cancelled: { dot: "#94a3b8", bg: "#f1f5f9", fg: "#475569" },
};

const TYPE_COLORS = { annual: "#2563eb", sick: "#dc2626", family: "#f59e0b", unpaid: "#64748b", study: "#7c3aed" };
const TYPE_FALLBACKS = ["#0891b2", "#db2777", "#65a30d", "#ea580c", "#4f46e5"];

// A named type keeps its colour; any other gets one from the palette by its place in the list.
export const typeColor = (type, types) => {
  const name = String(type?.name).toLowerCase();
  const known = Object.keys(TYPE_COLORS).find((word) => name.includes(word));
  return known ? TYPE_COLORS[known] : TYPE_FALLBACKS[Math.max(0, types.indexOf(type)) % TYPE_FALLBACKS.length];
};

export const typeIdOf = (t) => Number(t.leave_type_id ?? t.time_off_type_id);

export const REQUEST_COLUMNS = [
  { key: "employee", label: "Employee", visible: true },
  { key: "type", label: "Type", visible: true },
  { key: "dates", label: "Dates", visible: true },
  { key: "days", label: "Days", visible: true },
  { key: "status", label: "Status", visible: true },
  { key: "submitted", label: "Submitted", visible: false },
  { key: "note", label: "Note", visible: false },
  { key: "approved_by", label: "Approved by", visible: false },
  { key: "department", label: "Department", visible: false },
];

// Balance columns come from the company's leave types, so they are fed to useTableColumns as extra fields.
export const BALANCE_BASE_COLUMNS = [{ key: "employee", label: "Employee", visible: true }];
export const balanceFields = (types) => [
  ...types.map((t) => ({ field_id: typeIdOf(t), name: t.name })),
  { field_id: "pending", name: "Pending" },
  { field_id: "remaining", name: "Left" },
  { field_id: "carry", name: "Carry over", show_in_table: false },
  { field_id: "department", name: "Department", show_in_table: false },
];

export const REQUEST_GROUPS = {
  status: { label: "Status", get: (r) => r.statusLabel },
  type: { label: "Type", get: (r) => r.type_name },
  employee: { label: "Employee", get: (r) => r.employee_name },
  month: { label: "Month", get: (r) => monthLabel(r.start) },
};
export const BALANCE_GROUPS = { department: { label: "Department", get: (b) => b.department || "No department" } };

export const DEFAULT_REQUEST_FILTERS = { search: "", employee: "", type: "", groupBy: "status", period: "upcoming", sort: null };
export const DEFAULT_BALANCE_FILTERS = { search: "", department: "", groupBy: "", sort: null };

export const monthLabel = (date) =>
  new Date(`${date}T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

// ---- Requests ----
// Ids arrive as bigint strings and dates as timestamps, so both are made plain once here.
export const normalizeRequest = (r) => {
  const leave = normalizeLeave(r);
  return {
    ...leave,
    id: Number(r.leave_request_id),
    type_id: Number(r.time_off_type_id),
    statusLabel: STATUS_LABEL[r.status] || r.status,
    days: countLeaveDays(leave.start, leave.end, r.start_half_day, r.end_half_day),
    submitted: r.created_at ? dateOf(r.created_at) : null,
    custom_fields: r.custom_fields || {},
  };
};

export const isOpen = (r) => r.status === "pending" || r.status === "approved";

// People with approved leave today, counted once each.
export const offToday = (requests, today) =>
  new Set(requests.filter((r) => r.status === "approved" && r.start <= today && today <= r.end && !isWeekend(today)).map((r) => r.employee_id)).size;

const includes = (text, q) => (text || "").toString().toLowerCase().includes(q);

export const filterRequests = (rows, f, today) => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) =>
    (f.period === "past" ? r.end < today : f.period === "upcoming" ? r.end >= today : true)
    && (!f.employee || String(r.employee_id) === f.employee)
    && (!f.type || String(r.type_id) === f.type)
    && (!q || [r.employee_name, r.type_name, r.reason].some((v) => includes(v, q)))
  );
};

const REQUEST_ACCESSORS = {
  employee: (r) => r.employee_name,
  type: (r) => r.type_name,
  dates: (r) => r.start,
  days: (r) => r.days,
  status: (r) => r.statusLabel,
  submitted: (r) => r.submitted,
  note: (r) => r.reason,
  approved_by: (r) => r.approved_by_name,
  department: (r) => r.department,
};

const requestAccessors = (fields) => ({
  ...REQUEST_ACCESSORS,
  ...Object.fromEntries(fields.map((field) => [columnKeyFor(field), (r) => plainCustomValue(field, getFieldValue(r, field))])),
});

// Upcoming reads soonest first, Past most recent first, until a header is clicked.
export const sortRequests = (rows, sort, period, fields) =>
  sortByAccessor(rows, sort || { key: "dates", dir: period === "past" ? "desc" : "asc" }, requestAccessors(fields)[(sort || { key: "dates" }).key]);

export const requestExportColumns = (columns, fields) => {
  const accessors = requestAccessors(fields);
  return columns.map((c) => ({ key: c.key, label: c.label, visible: c.visible, value: accessors[c.key] }));
};

// What is left of the person's leave type once this request is taken: null with no entitlement loaded for that year.
export const balanceAfter = (request, balanceByKey, year) => {
  if (request.start.slice(0, 4) !== String(year)) return null;
  const row = balanceByKey[`${request.employee_id}:${request.type_id}`];
  return row ? Number(row.days_remaining) - request.days : null;
};

// ---- Balances ----
export const buildBalances = (summary) => {
  const byEmployee = new Map();
  summary.forEach((row) => {
    const id = Number(row.employee_id);
    if (!byEmployee.has(id)) {
      byEmployee.set(id, { id, name: row.employee_name, avatar: row.employee_avatar, department: row.department, byType: {}, pending: 0, remaining: 0, carry: 0, entitlements: [] });
    }
    const b = byEmployee.get(id);
    b.byType[Number(row.time_off_type_id)] = row;
    b.entitlements.push(Number(row.entitlement_id));
    b.pending += Number(row.days_pending);
    b.remaining += Number(row.days_remaining);
    b.carry += Number(row.carry_over_days);
  });
  return [...byEmployee.values()].sort((a, b) => a.name.localeCompare(b.name));
};

export const balanceByKey = (summary) =>
  Object.fromEntries(summary.map((row) => [`${row.employee_id}:${row.time_off_type_id}`, row]));

export const filterBalances = (rows, f) => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((b) => (!f.department || b.department === f.department) && (!q || includes(b.name, q)));
};

export const balanceAccessors = (types) => ({
  employee: (b) => b.name,
  cf_pending: (b) => b.pending,
  cf_remaining: (b) => b.remaining,
  cf_carry: (b) => b.carry,
  cf_department: (b) => b.department,
  ...Object.fromEntries(types.map((t) => [`cf_${typeIdOf(t)}`, (b) => Number(b.byType[typeIdOf(t)]?.days_approved ?? 0)])),
});

export const balanceExportColumns = (columns, types) => {
  const accessors = balanceAccessors(types);
  return columns.map((c) => {
    const typeId = Number(c.key.replace("cf_", ""));
    const isType = types.some((t) => typeIdOf(t) === typeId);
    return {
      key: c.key,
      label: c.label,
      visible: c.visible,
      value: isType
        ? (b) => { const e = b.byType[typeId]; return e ? `${Number(e.days_approved)} / ${Number(e.total_available)}` : null; }
        : accessors[c.key],
    };
  });
};

// ---- Calendar ----
export const monthDays = (month) => {
  const first = `${month}-01`;
  const next = new Date(`${first}T00:00:00`);
  next.setMonth(next.getMonth() + 1);
  return Array.from({ length: diffDays(first, toISODate(next)) }, (_, i) => addDays(first, i));
};

export const shiftMonth = (month, n) => {
  const d = new Date(`${month}-01T00:00:00`);
  d.setMonth(d.getMonth() + n);
  return toISODate(d).slice(0, 7);
};

// Each person's leave as blocks clipped to the month: { request, from, to } as day indexes.
export const calendarBlocks = (requests, days) =>
  requests.filter(isOpen).flatMap((request) => {
    const from = days.findIndex((d) => d >= request.start && d <= request.end);
    if (from < 0) return [];
    const to = days.findLastIndex((d) => d >= request.start && d <= request.end);
    return [{ request, from, to }];
  });

// How many people are away on each day (pending counts, so a manager sees a clash before approving).
export const offCounts = (requests, days) =>
  days.map((d) => (isWeekend(d) ? 0 : new Set(requests.filter((r) => isOpen(r) && r.start <= d && d <= r.end).map((r) => r.employee_id)).size));

// ---- Request form ----
// What the form shows under the dates: working days, the balance before and after, and who else is off.
export const requestPreview = ({ form, balanceMap, requests, year }) => {
  const days = countLeaveDays(form.startDate, form.endDate, form.startHalfDay, form.endHalfDay);
  const row = balanceMap[`${form.employee_id}:${form.timeOffTypeId}`];
  const inYear = form.startDate && form.startDate.slice(0, 4) === String(year);
  const left = row && inYear ? Number(row.days_remaining) : null;
  const others = [...new Set(requests
    .filter((r) => isOpen(r) && r.id !== form.timeOffId && String(r.employee_id) !== String(form.employee_id)
      && form.startDate <= r.end && r.start <= form.endDate)
    .map((r) => r.employee_name))];
  return { days, left, after: left == null ? null : left - days, others };
};
